
import en_UK from "./en_UK.json";
import { CacheManager } from "../utils";
import { NoseurIconElement, NoseurObject } from "@rivtn/noseur";

export interface FormControls {
    name: string;
    type?: string;
    title?: string;
    sub_title?: string;
    disabled?: boolean;
    editable?: boolean;
    strengthy?: boolean;
    placeholder?: string;
    value?: string | boolean;
    icon?: string | NoseurIconElement;
    extras?: {
        optionMap?: any;
        clearable?: boolean;
        onSelectOptions?: (value?: any) => void;
    };
    options?: {
        label: string;
        value: string;
    }[];
};

export interface LinkLocation {
    label: string;
    icon?: string;
    location: string;
};

export interface MatchDesignLabel {
    desc: string;
    title: string;
};

export type Labels = {
    logo: string;
    name: string;
    lang: string;
    rivtn: string;
    timezone: string;
    time_keeping: {
        ago: string;
        secs: string;
        days: string;
        hours: string;
        years: string;
        months: string;
        seconds: string;
        minutes: string;
        google_calender: string;
        outlook_calender: string;
    };
    common: {
        or: string;
        no: string;
        yes: string;
        all: string;
        back: string;
        prev: string;
        next: string;
        save: string;
        none: string;
        retry: string;
        error: string;
        close: string;
        token: string;
        upload: string;
        copied: string;
        choose: string;
        cancel: string;
        sort_by: string;
        refresh: string;
        continue: string;
        download: string;
        view_less: string;
        view_more: string;
        try_again: string;
        save_changes: string;
        default_logo: string;
        default_banner: string;
        click_to_choose: string;
        enable_visitor_acc_management: string;
    };
    numbers: {
        one: string;
        two: string;
        three: string;
    };
    chart: {
        type: string;
        zoom: string;
        reset: string;
        width: string;
        height: string;
        maintain_aspect_ration: string;
        chart_types: {
            label: string;
            value: string;
        }[]
    };
    compound: {
        menu_locations: LinkLocation[];
        quick_locations: LinkLocation[];
        top_navbar: {
            search: string;
            select_a_language: string;
            popover: {
                notification: {
                    all: string;
                    today: string;
                    unread: string;
                    delete: string;
                    earlier: string;
                    yesterday: string;
                    mark_as_read: string;
                    notifications: string;
                    mark_as_unread: string;
                    mark_all_as_read: string;
                    no_notifications: string;
                    mark_all_read_success: string;
                    see_all_notifications: string;
                };
                profile: {
                    signout: string;
                    dark_mode: string;
                    change_plan: string;
                    view_profile: string;
                    edit_profile: string;
                    manage_website: string;
                    expand_sidebar: string;
                    tournament_feed: string;
                    help_and_support: string;
                    collapse_sidebar: string;
                };
            };
        };
        popover: {
            create_profile: {
                form: FormControls[];
                create_profile: string;
                create_your_profile: string;
                has_space_or_special: string;
                missing_required_fields: string;
            };
        };
        account_settings: {
            menu: {
                menu: string;
                home: LinkLocation;
                media: LinkLocation;
                merch: LinkLocation;
                design: LinkLocation;
                gallery: LinkLocation;
                general: LinkLocation;
                billing: LinkLocation;
                schedule: LinkLocation;
                sessions: LinkLocation;
                manage_account: string;
                profile_website: string;
                plan_and_billing: string;
                manage_plan: LinkLocation;
                manage_data: LinkLocation;
                edit_profile: LinkLocation;
                achievements: LinkLocation;
                team_history: LinkLocation;
                notifications: LinkLocation;
                interest_and_ai: LinkLocation;
                social_accounts: LinkLocation;
                connected_hosts: LinkLocation;
            };
        };
        landing: {
            hello: string;
            verify_email: {
                resend: string;
                resend_link: string;
                error_occur: string;
                verify_profile: string;
                verify_link_sent: string;
                verify_successful: string;
                verify_your_profile: string;
                verify_profile_short: string;
            };
            tournament_feed: {
                by: string;
                register: string;
                view_detail: string;
                view_result: string;
                controls: {
                    ended: string;
                    ongoing: string;
                    upcoming: string;
                }
            };
        };
    };
    notification: {
        action_labels: {
            message: string;
            view_join_code: string;
            reject_team_invite: string;
            accept_team_invite: string;
            cancel_team_invite: string;
            view_member_profile: string;
            open_support_ticket: string;
        };
    };
    page_design: {
        reset: string;
        randomize: string;
        customize: string;
        select_design: string;
        update_design: string;
        mobile_preview: string;
        desktop_preview: string;
        scroll_down_for_more: string;
        mobile: {
            ios: string;
            name: string;
            debug: string;
            source: string;
            android: string;
            release: string;
            version: string;
            ios_down: string;
            config_anal: string;
            ios_min_ver: string;
            react_native: string;
            package_name: string;
            remote_debug: string;
            android_down: string;
            android_min_sdk: string;
            enable_analytics: string;
            make_auth_required: string;
            enable_notifications: string;
            view_analytics_dashboard: string;
            configure_android_keystore: string;
            enable_user_team_management: string;
            dialog: {
                keystore: {
                    desc: string;
                    info: string;
                    title: string;
                    upload: string;
                    remove: string;
                    keystore: string;
                    generate: string;
                };
            };
        };
    };
    import_export: {
        title: string;
        select: string;
        export: string;
        copy_from: string;
        select_file: string;
        confirm_import: string;
        import_settings: string;
        export_settings: string;
        export_settings_desc: string;
        import_settings_desc: string;
        choose_settings_file: string;
        cannot_import_settings: string;
        allow_checkbox: NoseurObject<string>;
    };
    account: {
        plan_and_billing: {
            title: string;
            manage_plan: {
                off: string;
                desc: string;
                title: string;
                upgrade: string;
                downgrade: string;
                current_plan: string;
                report_message: string;
                plan_change_token_sent: string;
                per_month_billed_yearly: string;
                per_month_billed_monthly: string;
                duration: {
                    yearly: string;
                    monthly: string;
                };
                dialog: {
                    upgrade_plan_using: string;
                    change_payment_method: string;
                    downgrade: {
                        desc: string;
                        desc2: string;
                        title: string;
                        cost_info: string;
                        success_message: string;
                        article: {
                            label: string;
                            location: string;
                        };
                    };
                    upgrade: {
                        desc: string;
                        desc2: string;
                        title: string;
                        others: string;
                        success_message: string;
                        select_payment_method: string;
                        article: {
                            label: string;
                            location: string;
                        };
                    };
                };
            };
            billing: {
                plan: string;
                title: string;
                upgrade_to: string;
                auto_renew: string;
                date_range: string;
                auto_renew_desc: string;
                payment_methods: string;
                billing_history: string;
                download_history: string;
                auto_renew_updated: string;
                payment_method_deleted: string;
                receive_renew_payment_link: string;
                default_payment_method_updated: string;
                payment_method_label: {
                    card: string;
                    payment_link: string;
                };
                table: {
                    to: string;
                    headers: {
                        date: string;
                        amount: string;
                        details: string;
                        receipt: string;
                        payment_method: string;
                    };
                    duration: {
                        YEARLY: string;
                        MONTHLY: string;
                    };
                };
                dialog: {
                    add_new_card: {
                        title: string;
                    }
                };
            };
        };
        manage_account: {
            title: string;
            edit_profile: {
                title: string;
                username: string;
                last_name: string;
                first_name: string;
                profile_url: string;
                phone_number: string;
                email_address: string;
                update_success: string;
                country_determinant: string;
                update_email_name_on_rivtn: string;
            };
            interests_ai: {
                desc: string;
                title: string;
                your_interests: string;
                update_success: string;
                type_and_press_enter: string;
                use_data_to_train_omnehost: string;
                use_interests_to_guide_media_gen: string;
                ai_gen_prompts: {
                    team_ai_logo_gen_prompt: string;
                    profile_ai_logo_gen_prompt: string;
                    profile_ai_banner_gen_prompt: string;
                    tournament_ai_logo_gen_prompt: string;
                    profile_ai_passport_gen_prompt: string;
                    profile_ai_half_body_gen_prompt: string;
                    profile_ai_self_anim_gen_prompt: string;
                    tournament_ai_trophy_gen_prompt: string;
                    tournament_ai_winning_gen_prompt: string;
                    tournament_host_ai_logo_gen_prompt: string;
                };
            };
            social_accounts: {
                desc: string;
                title: string;
                update_success: string;
                accounts_keys: {
                    psn_id: string;
                    twitch: string;
                    xbox_id: string;
                    twitter: string;
                    discord: string;
                    youtube: string;
                    steam_id: string;
                    telegram: string;
                    facebook: string;
                    instagram: string;
                };
            };
            sessions: {
                on: string;
                desc: string;
                title: string;
                delete: string;
                revoke: string;
                unknown: string;
                location: string;
                signed_in: string;
                last_activity: string;
                active_session: string;
            };
            manage_data: {
                desc: string;
                title: string;
                manage_your_data: string;
                request_your_data: string;
                clear_omnehost_data: string;
                your_last_data_request: string;
                delete_omnehost_account: string;
                data_download_request_sent: string;
                dialog: {
                    token_required: string;
                    download_your_data: string;
                    clear: {
                        title: string;
                        desc1: string;
                        desc2: string;
                        success: string;
                        token_sent: string;
                        confirm_label: string;
                        article: LinkLocation;
                    };
                    delete: {
                        title: string;
                        desc1: string;
                        desc2: string;
                        success: string;
                        token_sent: string;
                        confirm_label: string;
                        article: LinkLocation;
                    };
                };
            };
        };
        profile_website: {
            title: string;
            update_successful: string;
            general: {
                title: string;
                basic: {
                    title: string;
                    username: string;
                    account_id: string;
                    display_name: string;
                    profile_details: string;
                    visibility: FormControls;
                };
                website_pages: {
                    home: string;
                    title: string;
                    label: string;
                    visible: string;
                    gallery: string;
                    schedule: string;
                    home_page: string;
                    merch_page: string;
                    merchandise: string;
                    achievement: string;
                    team_history: string;
                    gallery_page: string;
                    schedule_page: string;
                    achievement_page: string;
                    team_history_page: string;
                };
            };
            media: {
                title: string;
                upload: {
                    dialog: {
                        title: string;
                        generate_again: string;
                        select_another: string;
                        no_media_selected_or_generated: string;
                    };
                    popover: {
                        generate_with_ai: string;
                        upload_from_device: string;
                    };
                };
                sections: {
                    key: string;
                    desc: string;
                    desc2: string;
                    title: string;
                }[];
            };
            home: {
                title: string;
                enable_ad: string;
                show_sponsor: string;
                show_schedules: string;
                show_top_gallery: string;
                show_twitter_feed: string;
                show_twitch_stream: string;
                show_instagram_feed: string;
                show_top_activities: string;
                upload_sponsor_logos: string;
                dialog: {
                    small_preview: string;
                    large_preview: string;
                    medium_preview: string;
                    select_another: string;
                    no_media_selected: string;
                    upload_home_sponsor_logo: string;
                };
            };
            merch: {
                title: string;
                preview: string;
                enable_ad: string;
                manage_store: string;
                connect_store: string;
            };
            achievements: {
                title: string;
                winnings: string;
                trophies: string;
                enable_ad: string;
                show_graphs: string;
                show_trails: string;
                team_achievements: string;
                leaderboard_ranking: string;
                show_team_winning_trails: string;
                show_team_winning_graphs: string;
                show_team_trophies_trails: string;
                show_team_trophies_graphs: string;
            };
            schedule: {
                title: string;
                enable_ad: string;
                show_team_past_matches: string;
                show_personal_past_matches: string;
                show_team_ongoing_and_upcoming_matches: string;
                show_personal_ongoing_and_upcoming_matches: string;
            };
            team_history: {
                title: string;
                enable_ad: string;
                hide_team: string;
                show_team: string;
                show_current_team: string;
                show_previous_team: string;
            };
            gallery: {
                title: string;
                enable_ad: string;
                dialog: {
                    add_update: {
                        add: string;
                        update: string;
                        caption: string;
                        action_label: string;
                        action_label_1: string;
                        action_label_2: string;
                        add_successful: string;
                        action_location: string;
                        add_website_hero: string;
                        action_location_1: string;
                        action_location_2: string;
                        update_successful: string;
                        no_media_selected: string;
                        update_website_hero: string;
                        add_media_to_gallery: string;
                        update_media_in_gallery: string;
                    };
                    delete: {
                        desc: string;
                        title: string;
                        successful: string;
                        delete_website_hero: string;
                    };
                };
            };
            design: {
                title: string;
            };
        };
    };
    dashboard: {
        hello: string;
        intro: string;
        pending: string;
        earnings: string;
        available: string;
        in_progress: string;
        chart_title: string;
        tournaments: string;
        hide_balance: string;
        expected_to_win: string;
        available_balance: string;
        your_matches_graph: string;
        ongoing_tournaments: string;
        view_all_tournaments: string;
        your_active_tournaments: string;
        empty_tournaments_parts: {
            and: string;
            for_a: string;
            search: string;
            create: string;
            you_dont: string;
        };
    };
    teams: {
        title: string;
        join_team: string;
        disbanded_teams: string;
        create_new_team: string;
        invite_cancelled: string;
        view_active_teams: string;
        view_disbanded_teams: string;
        query_inputs: FormControls[];
        dropdown_form_control: FormControls;
        team_type_presentable: NoseurObject<string>;
        sorts: {
            label: string;
            value: string;
        }[];
        empty_label_parts: {
            create: string;
            to_bring: string;
            you_do_not: string;
            join_a_team: string;
            no_disbanded_teams: string;
        };
        popover: {
            join_team: {
                using_qr_code: string;
                using_join_code: string;
                using_id_and_passcode: string;
            };
            invite_member: {
                send_invite: string;
                share_qr_code: string;
                share_join_code: string;
            };
        };
        dialog: {
            share_to: string;
            confirm_team: {
                to_continue: string;
                name_does_not_match: string;
                mem_name_does_not_match: string;
                team_name_control: FormControls;
            };
            create_team: {
                name: string;
                title: string;
                passcode: string;
                create_team: string;
                description: string;
                logo_required: string;
                team_created_successfully: string;
                name_description_required: string;
            };
            leave_team: {
                desc: string;
                title: string;
            };
            disband_team: {
                desc: string;
                title: string;
                effect_desc: string;
                article: {
                    label: string;
                    location: string;
                };
            };
            reactivate_team: {
                desc: string;
                title: string;
            };
            share_qr_code: {
                desc: string;
                title: string;
                download_qr_code: string;
            };
            share_join_code: {
                desc: string;
                title: string;
                expiry_updated: string;
                copy_join_code: string;
                expiry_date: FormControls;
            };
            invite_member: {
                desc: string;
                title: string;
                note: FormControls;
                send_invite: string;
                invite_sent: string;
                member: FormControls;
                expiry_date: FormControls;
            };
            join_with_qr_code: {
                title: string;
                join_team: string;
                scan_qr_code: string;
                invalid_qr_code: string;
                upload_team_qr_code: string;
            };
            join_with_join_code: {
                title: string;
                join_team: string;
                join_code: FormControls;
            };
            join_with_id_and_passcode: {
                title: string;
                join_team: string;
                team_id: FormControls;
                team_passcode: FormControls;
            };
            accept_join_term: {
                desc: string;
                note: string;
                title: string;
                confirm: string;
                disclaimer: string;
                accept_team_rule: FormControls;
                accept_omnehost_policy: FormControls;
            };
        };
        analytics: {
            title: string;
            date_range: string;
            team_anals: string;
            tournaments: string;
            tournament_s: string;
            achievements: string;
            top_programs: string;
            total_earnings: string;
            top_team_earners: string;
            country_currency: string;
            tournaments_entered: string;
            earned_within_x_days: string;
            achievements_within_x_days: string;
            chart_labels: {
                earnings: string;
                tournaments: string;
                achievements: string;
            };
            no_programs_parts: {
                participate: string;
                no_pros_found: string;
                in_tournament_to: string;
            };
            earnings: {
                you: string;
                owner: string;
                title: string;
                ads_earning: string;
                merch_earnings: string;
                total_earnings: string;
                earning_anals_for: string;
                tournament_earnings: string;
                earned_from_rivtn_ads: string;
                earned_from_tournaments: string;
                earned_from_merch_sales: string;
                transactions: {
                    in: string;
                    out: string;
                    title: string;
                    export: string;
                    transactions: string;
                    view_all_trans: string;
                    last_10_earning: string;
                    query_inputs: FormControls[];
                    table: {
                        headers: {
                            flow: string;
                            date: string;
                            amount: string;
                            source: string;
                            recipient: string;
                        };
                    };
                    empty_label_parts: {
                        participate: string;
                        in_tournament_or: string;
                        no_earning_found: string;
                        create_merch_store: string;
                        to_generate_incomes: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                };
            };
            tournaments_page: {
                won: string;
                lost: string;
                title: string;
                export: string;
                entered: string;
                all_tours: string;
                tours_won: string;
                tours_skip: string;
                tours_lost: string;
                participate: string;
                tours_entered: string;
                tours_participation: string;
                query_inputs: FormControls[];
                table: {
                    headers: {
                        status: string;
                        tournament: string;
                        participants: string;
                        registration_date: string;
                    };
                };
                empty_label_parts: {
                    in_a_tour: string;
                    participate: string;
                    no_records_found: string;
                };
                sorts: {
                    label: string;
                    value: string;
                }[];
            };
            achievements_page: {
                title: string;
                others: string;
                export: string;
                trophies: string;
                team_awards: string;
                first_place: string;
                third_place: string;
                second_place: string;
                all_achievements: string;
                total_trophies_won: string;
                query_inputs: FormControls[];
                top_of_the_tournament: string;
                first_runner_up_position: string;
                second_runner_up_position: string;
                table: {
                    headers: {
                        badge: string;
                        tournament: string;
                        achievement: string;
                        date_achieved: string;
                    };
                };
                empty_label_parts: {
                    in_a_tour: string;
                    participate: string;
                    no_achievements_found: string;
                };
                sorts: {
                    label: string;
                    value: string;
                }[];
            };
        };
        settings: {
            team: string;
            general: {
                title: string;
                basic: {
                    name: string;
                    title: string;
                    country: string;
                    team_id: string;
                    passcode: string;
                    homepage: string;
                    visibility: string;
                    description: string;
                    email_address: string;
                    update_success: string;
                };
                moderators: {
                    title: string;
                    moderating_since: string;
                    update_moderators: string;
                    transfer_ownership: string;
                    search_and_select_member: string;
                    moderator_updated_successfully: string;
                    dialog: {
                        transfer: {
                            desc: string;
                            title: string;
                            team_name: string;
                            transfer_successful: string;
                            search_and_select_team_members: string;
                        };
                        remove: {
                            desc: string;
                            title: string;
                            enter: string;
                            team_name: string;
                            demote_moderator: string;
                            remove_successful: string;
                        };
                    };
                };
                team_earnings: {
                    city: string;
                    state: string;
                    title: string;
                    address: string;
                    country: string;
                    who_receives: string;
                    address_info: string;
                    update_success: string;
                    delivery_contact: string;
                    accept_policy: {
                        accept: string;
                        omne_policy: {
                            label: string;
                            location: string;
                        };
                    };
                    info: {
                        part_1: string;
                        part_2: string;
                        part_3: string;
                        part_4: string;
                        part_5: string;
                    };
                    receivers: {
                        owner_you: string;
                        selected_members: string;
                        select_a_moderator: string;
                        split_equally_bet_mods: string;
                        split_equally_bet_mems: string;
                    };
                };
                social_networks: {
                    title: string;
                    update_success: string;
                    enter_team_usernames: string;
                };
                banner_medias: {
                    title: string;
                    banner_1: string;
                    banner_2: string;
                    banner_3: string;
                    make_primary: string;
                    update_success: string;
                };
                website_pages: {
                    news: string;
                    home: string;
                    merch: string;
                    title: string;
                    label: string;
                    visible: string;
                    gallery: string;
                    members: string;
                    schedule: string;
                    news_page: string;
                    home_page: string;
                    merch_page: string;
                    activities: string;
                    achievements: string;
                    gallery_page: string;
                    members_page: string;
                    schedule_page: string;
                    activities_page: string;
                    achievements_page: string;
                    update_successful: string;
                };
                rules: {
                    title: string;
                    team_rules: string;
                    update_rules: string;
                    update_success: string;
                };
            };
            home: {
                title: string;
                show_sponsors: string;
                show_top_acts: string;
                enable_rivtn_ad: string;
                show_top_gallery: string;
                show_top_members: string;
                update_successful: string;
                show_twitter_feed: string;
                show_twitch_stream: string;
                show_team_schedules: string;
                show_instagram_feed: string;
                upload_team_sponsors: string;
                upload_team_sponsor_logo: string;
            };
            members: {
                you: string;
                role: string;
                title: string;
                joined: string;
                enable_ad: string;
                last_left: string;
                first_joined: string;
                update_success: string;
                show_prev_mems: string;
                previous_member: string;
                update_successful: string;
                inc_prev_team_mems: string;
                re_invite_successfully_sent: string;
                member: {
                    twitch: string;
                    youtube: string;
                    country: string;
                    twitter_x: string;
                    instagram: string;
                    state_region: string;
                    display_name: string;
                    show_recent_achs: string;
                    show_profile_link: string;
                    hide_from_mem_page: string;
                    profile_description: string;
                };
                dialog: {
                    kick: {
                        desc: string;
                        title: string;
                        mems_name: string;
                        give_the_boot: string;
                        remove_successful: string;
                    };
                };
            };
            news: {
                title: string;
                enable_ad: string;
                publish_a_news: string;
                update_successful: string;
                sorts: {
                    label: string;
                    value: string;
                }[];
                dialog: {
                    create_edit: {
                        title: string;
                        update: string;
                        delete: string;
                        publish: string;
                        update_or_delete: string;
                        create_team_news: string;
                        delete_successful: string;
                        update_successful: string;
                        publish_successful: string;
                        content_in_markdown: string;
                    };
                    delete: {
                        desc: string;
                        title: string;
                    };
                };
            };
            merch: {
                title: string;
                update_successful: string;
            };
            activities: {
                title: string;
                members: string;
                timeline: string;
                enable_ad: string;
                activities: string;
                show_trails: string;
                show_graphs: string;
                tournaments: string;
                achievements: string;
                show_trophies: string;
                update_successful: string;
            };
            gallery: {
                title: string;
                update_successful: string;
            };
            website: {
                title: string;
                design_update_successful: string;
            };
        };
    };
    tournaments: {
        online_tournaments: string;
        back_to_tournament: string;
        registration_failed: string;
        registration_successful: string;
        team_types: {
            label: string;
            value: string;
        }[];
        sorts: {
            label: string;
            value: string;
        }[];
        filter: {
            clear: string;
            filter: string;
            show_x_more: string;
            form: FormControls[];
            familiar_orgs: string;
            collapse_filter: string;
        };
        navigators: {
            past_tournaments: string;
            your_tournaments: string;
            ongoing_tournaments: string;
            upcoming_tournaments: string;
        };
        empty_label_parts: {
            create: string;
            or_update_search: string;
            no_tournament_matches: string;
        };
        tournament: {
            pass: string;
            host: string;
            free: string;
            ends: string;
            visit: string;
            status: string;
            closes: string;
            program: string;
            pending: string;
            withdraw: string;
            location: string;
            register: string;
            registered: string;
            view_result: string;
            announced_on: string;
            tournament_day: string;
            announcement_on: string;
            removed_because: string;
            withdraw_because: string;
            registration_fee: string;
            participants_joined: string;
            prize_pool_distribution: string;
            statuses: {
                ended: string;
                ongoing: string;
                starting_at: string;
                registration_in_progress: string;
                ended_pending_result_announcement: string;
                ended_pending_prize_pool_distribution: string;
            };
        };
        dialog: {
            programs: {
                done: string;
                reset_filter: string;
                search_program: string;
                top_in_country: string;
                upcoming_tournaments: string;
            };
            confirm_tournament: {
                tour_name: string;
                to_continue: string;
                name_does_not_match: string;
            };
            register: {
                proceed: string;
                required_info: string;
                free_to_enter: string;
                proceed_to_pay: string;
                important_info: string;
                tournament_rule: string;
                select_your_team: string;
                something_to_note: string;
                you_shall_not_pass: string;
                registration_failed: string;
                select_x_team_member: string;
                required_fields_missing: string;
                accept_tournament_terms: string;
                select_x_sub_team_member: string;
                your_account_will_be_used: string;
                refund_policy: {
                    info: string;
                    article_label: string;
                    article_link: string;
                };
                prize_info: {
                    physical_prize_not_in_country: string;
                    monetary_prize_not_in_currency: string;
                    claiming_physical_prize: {
                        label: string;
                        location: string;
                    };
                    claiming_monetary_prize: {
                        label: string;
                        location: string;
                    };
                };
                participant_requirements: {
                    professional: string;
                    top_xin_program: string;
                    non_professional: string;
                    premium_members_only: string;
                    allow_incomplete_team: string;
                    previous_tournament_participant: string;
                    previous_participant_in_tournaments_from_same_host: string;
                };
                payment: {
                    charge_fee: string;
                    total_amount: string;
                    payment_summary: string;
                    registration_fee: string;
                };
                success: {
                    desc: string;
                    title: string;
                    view_pass: string;
                    save_dates: string;
                };
            };
            withdraw: {
                desc: string;
                title: string;
                reason_input: FormControls;
                tournament_name_input: FormControls;
                withdrawal_request_submitted: string;
            };
            withdrawn: {
                your_reason: string;
                remove_title: string;
                withdraw_title: string;
                reg_was_refunded: string;
            };
        };
        your_tournament: {
            loading: string;
            tournament: string;
            home: {
                place: string;
                title: string;
                matches: string;
                tour_day: string;
                you_have: string;
                standings: string;
                participant: string;
                you_have_won: string;
                matches_left: string;
                use_banner_bg: string;
                and_lost_match: string;
                and_below_others: string;
                total_of_in_tour: string;
                above_other_parts: string;
                you_currently_rank_top: string;
                upcoming_oncoming_matches: string;
                you_have_matches_in_total: string;
                may_the_universe_be_with_you: string;
                scoring_system_map: NoseurObject<string>;
                empty_parts: {
                    you_have_no: string;
                    for_past_matches: string;
                    matches_and_reportings: string;
                };
            };
            activities: {
                title: string;
            };
            calender: {
                title: string;
                show_schedule: string;
                design_headers: MatchDesignLabel;
                show_other_participant_schedule: string;
            };
            matches_and_reporting: {
                vs: string;
                group: string;
                title: string;
                location: string;
                view_result: string;
                forfeit_match: string;
                submit_result: string;
                no_match_found: string;
                show_past_matches: string;
                show_ongoing_matches: string;
                show_upcoming_matches: string;
                score_labels: NoseurObject<string>;
                ended_match_score_labels: NoseurObject<string>;
                designs_headers: {
                    ended: string;
                    ongoing: string;
                    upcoming: string;
                    cancelled: string;
                    solo_match: MatchDesignLabel;
                    lineup_solo: MatchDesignLabel;
                    matches_won: MatchDesignLabel;
                    matches_left: MatchDesignLabel;
                    tour_ranking: MatchDesignLabel;
                    versus_match: MatchDesignLabel;
                    lineup_versus: MatchDesignLabel;
                    lineup_member_solo: MatchDesignLabel;
                    multi_versus_match: MatchDesignLabel;
                    lineup_multi_versus: MatchDesignLabel;
                    lineup_member_versus: MatchDesignLabel;
                    lineup_member_multi_versus: MatchDesignLabel;
                };
                match: {
                    won: string;
                    lost: string;
                    draw: string;
                    match: string;
                    close: string;
                    scoring_system_labels: NoseurObject<string>;
                    result: {
                        you: string;
                        open: string;
                        view: string;
                        watch: string;
                        remove: string;
                        accept: string;
                        listen: string;
                        accepted: string;
                        submitted_by: string;
                        back_to_lineup: string;
                        your_submitted_proof: string;
                        team_mem_sub_result: string;
                        your_submitted_proof_note: string;
                        empty_result_parts: {
                            submit: string;
                            no_result: string;
                            for_scoring: string;
                        };
                    },
                    dialogs: {
                        submit_request: {
                            you: string;
                            note: string;
                            title: string;
                            submit: string;
                            opponent: string;
                            solo_desc: string;
                            versus_desc: string;
                            upload_proofs: string;
                            score_or_comment: string;
                            multi_versus_desc: string;
                            submission_successful: string;
                            search_and_select_team_mem: string;
                            article: {
                                label: string;
                                location: string;
                            };
                        };
                        forfeit: {
                            desc: string;
                            title: string;
                            reason: string;
                            forfeit: string;
                            successfully_forfeited: string;
                            how_it_affects: {
                                label: string;
                                location: string;
                            };
                        };
                        delete_proof: {
                            desc: string;
                            title: string;
                        };
                    };
                };
            };
        };
    };
    host: {
        title: string;
        create_host: string;
        retired_hosts: string;
        view_active_hosts: string;
        view_retired_hosts: string;
        query_inputs: FormControls[];
        sorts: {
            label: string;
            value: string;
        }[];
        empty_label_parts: {
            create: string;
            you_do_not: string;
            no_retired_hosts: string;
            or_update_search: string;
        };
        controls: {
            chatroom: string;
            analytics: string;
            notifications: string;
            leave_hosting: string;
            retire_hosting: string;
            change_settings: string;
            reactivate_hosting: string;
            manage_tournaments: string;
            generate_mobile_app: string;
            create_new_tournament: string;
        };
        dialog: {
            confirm_host: {
                host_name: string;
                to_continue: string;
                name_does_not_match: string;
            };
            create: {
                name: string;
                title: string;
                id_number: string;
                issue_date: string;
                expiry_date: string;
                create_host: string;
                host_type: FormControls;
                means_of_id: FormControls;
                upload_proof_of_id: string;
                host_created_successfully: string;
                country_of_operation: FormControls;
            };
            leave: {
                desc: string;
                title: string;
            };
            retire: {
                desc: string;
                title: string;
            };
            reactivate: {
                desc: string;
                title: string;
            };
        };
        create_tournament: {
            publish: string;
            save_draft: string;
            save_changes: string;
            update_tournament: string;
            go_to_manager_mode: string;
            create_new_tournament: string;
            dialog: {
                cancel: {
                    desc: string;
                    title: string;
                };
                published: {
                    desc: string;
                    title: string;
                    copy_link: string;
                    visit_page: string;
                    go_to_manager_mode: string;
                };
            };
            details: {
                name: string;
                title: string;
                search: string;
                country: string;
                location: string;
                description: string;
                invite_only: string;
                is_recurring: string;
                last_organized: string;
                from_same_host: string;
                from_other_host: string;
                physical_location: string;
                publish_as_template: string;
                password_protection: string;
                make_tournament_private: string;
                copy_existing_tournament: string;
            };
            program_and_participant: {
                title: string;
                search: string;
                platforms: string;
                copyright: string;
                theme_color: string;
                request_program: string;
                non_professional: string;
                pending_approval: string;
                professional_only: string;
                only_premium_users: string;
                create_new_program: string;
                participant_per_reg: string;
                custom_no_of_players: string;
                top_x_rank_in_program: string;
                allow_extra_team_member: string;
                search_and_select_program: string;
                previous_tournament_participants: string;
                allow_team_with_incomplete_member: string;
                participants_in_other_tours_from_same_host: string;
                dialog: {
                    create_program: {
                        logo: string;
                        name: string;
                        title: string;
                        banner: string;
                        create: string;
                        account: string;
                        supports: string;
                        full_name: string;
                        platforms: string;
                        copyright: string;
                        short_name: string;
                        physical_location: string;
                        supports_x_accounts: string[];
                    };
                    request_program: {
                        name: string;
                        desc: string;
                        title: string;
                        company: string;
                        send_request: string;
                        program_info_page: string;
                    };
                };
            };
            media: {
                title: string;
                note_can_add_more: string;
                select_program_icon: string;
                select_program_banner: string;
                note_selected_media_copyright: string;
            };
            dates: {
                title: string;
                end_date: string;
                start_date: string;
                tournament: string;
                registration: string;
                post_participation: string;
                result_announcement_date: string;
                winner_announcement_date: string;
                prize_pool_dist_end_date: string;
                prize_pool_dist_start_date: string;
            };
            registration: {
                title: string;
                bear_reg_cost: string;
                show_reg_count: string;
                max_participants: string;
                registration_fees: string;
                add_another_country: string;
                note_max_participants_optional: string;
                prevent_already_engaged_participants: string;
                note_tour_available_in_listed_countries: string;
            };
            custom_fields: {
                text: string;
                date: string;
                title: string;
                check: string;
                number: string;
                options: string;
                add_new_custom_field: string;
                dialog: {
                    add_edit: {
                        add: string;
                        type: string;
                        label: string;
                        update: string;
                        options: string;
                        required: string;
                        add_new_option: string;
                        missing_fields: string;
                        update_custom_field: string;
                        add_new_custom_field: string;
                    };
                };
            };
            prize_pool: {
                note: string;
                pools: string;
                title: string;
                no_prize: string;
                trophies: string;
                top_winners: string;
                physical_prize: string;
                show_prize_pool_in_listing: string;
                dialog: {
                    manage_prize: {
                        add: string;
                        name: string;
                        title: string;
                        update: string;
                        remove: string;
                        position: string;
                        add_trophy: string;
                        prize_type: string;
                        detail_link: string;
                        how_to_claim: string;
                        update_trophy: string;
                        add_top_winner: string;
                        missing_fields: string;
                        update_top_winner: string;
                        select_prize_image: string;
                        avail_in_all_countries: string;
                        leave_empty_for_no_prize: string;
                        prize_type_labels: NoseurObject<string>;
                    };
                };
            };
            stage_and_format: {
                title: string;
                read_more: string;
                stage_note: string;
                choose_stage: string;
                select_format: string;
                stage_update_info: string;
                stage_info: {
                    label: string;
                    read_more: string;
                    article_link: string;
                };
                format_info: {
                    label: string;
                    read_more: string;
                    article_link: string;
                };
                stages: {
                    desc: string;
                    label: string;
                    value: string;
                    article_link: string;
                }[];
                formats: {
                    desc: string;
                    label: string;
                    value: string;
                    article_link: string;
                }[];
            };
            scoring_and_tie_breaking: {
                title: string;
                score: string;
                read_more: string;
                score_label: string;
                tie_breaker: string;
                score_system: string;
                starting_score: string;
                submission_name: string;
                x_per_match_win: string;
                x_per_match_loss: string;
                x_per_match_draw: string;
                higher_value_wins: string;
                show_on_match_pages: string;
                track_multiple_score: string;
                this_is_primary_score: string;
                choose_scoring_system: string;
                select_tie_breaker_mechanism: string;
                allow_participant_submit_score: string;
                choose_initial_score_for_tie_breaking: string;
                score_system_info: {
                    label: string;
                    read_more: string;
                    article_link: string;
                };
                tie_breaker_info: {
                    label: string;
                    read_more: string;
                    article_link: string;
                };
                scoring_systems: {
                    desc: string;
                    label: string;
                    value: string;
                    article_link: string;
                }[];
                tie_breakers: {
                    desc: string;
                    label: string;
                    value: string;
                    article_link: string;
                }[];
            };
            final: {
                prev: string;
                next: string;
                desc: string;
                publish: string;
                go_to_manager_mode: string;
            };
            errors: {
                name_required: string;
                country_required: string;
                location_required: string;
            };
        };
        manage_tournament: {
            messages: {
                no_logo: string;
                no_banner: string;
                no_program: string;
                add_program: string;
                update_media: string;
            };
            dialog: {
                create_edition_failed: {
                    desc: string;
                    title: string;
                }
            };
            top_bar: {
                date: string;
                visit: string;
                search: string;
                edition: string;
                update_media: string;
                change_dates: string;
                change_details: string;
                create_edition: string;
                edit_in_wizard_mode: string;
                tour_snap: MatchDesignLabel;
            };
            side_bar: {
                finance: string;
                scoring: string;
                settings: string;
                chat_room: string;
                analytics: string;
                participants: string;
                notifications: string;
                match_fixtures: string;
                result_prize_distro: string;
                stage_format_bracket: string;
            };
            participants: {
                export: string;
                substitutes: string;
                no_participants: string;
                seed_participants: string;
                import_participants: string;
                invite_participants: string;
                query_inputs: FormControls[];
                historical_participants: string;
                overview_snap: MatchDesignLabel;
                detail: {
                    seed: string;
                    team: string;
                    status: string;
                    member: string;
                    send_gift: string;
                    removal_date: string;
                    removal_reason: string;
                    refunded_amount: string;
                    withdrawal_date: string;
                    registration_cost: string;
                    registration_date: string;
                    withdrawal_reason: string;
                    send_private_message: string;
                    custom_fields_answers: string;
                    remove_from_tournament: string;
                    participant_snap: MatchDesignLabel;
                    participant_member_snap: MatchDesignLabel;
                    participant_members_snap: MatchDesignLabel;
                    removed_participant_snap: MatchDesignLabel;
                    withdrawn_participant_snap: MatchDesignLabel;
                };
                table: {
                    headers: {
                        name: string;
                        seed: string;
                        status: string;
                        registration_date: string;
                        registration_cost: string;
                    };
                    empty_label_parts: {
                        or: string;
                        have_no: string;
                        copy_link: string;
                        send_invite: string;
                        create_media: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                };
                dialog: {
                    name_must_match: string;
                    send_gift: {
                        desc: string;
                        send: string;
                        title: string;
                        currency: string;
                        amount_max: string;
                        participant_name: string;
                        gift_sent_successfully: string;
                    };
                    remove_from_tournament: {
                        desc: string;
                        title: string;
                        reason: string;
                        participant_name: string;
                        participant_removed_successfully: string;
                    };
                    seed_participants: {
                        desc: string;
                        title: string;
                        update_seeding: string;
                        manual_seeding: string;
                        seed_by_registration: string;
                        auto_seed_by_randomization: string;
                        table: {
                            headers: {
                                name: string;
                                seed_number: string;
                                registration_date: string;
                            };
                        };
                    };
                    export_participants: {
                        seed: string;
                        desc: string;
                        name: string;
                        title: string;
                        export: string;
                        status: string;
                        refund_amount: string;
                        registration_date: string;
                        registration_amount: string;
                        custom_fields_answers: string;
                        includes_amount_currencies: string;
                    };
                    invite_participants: {
                        desc: string;
                        title: string;
                        send_invite: string;
                        email_address: string;
                        add_new_email_address: string;
                    };
                };
            };
            match_fixtures: {
                group: string;
                match: string;
                export: string;
                card_view: string;
                data_view: string;
                calendar_view: string;
                add_new_match: string;
                historic_matches: string;
                query_inputs: FormControls[];
                match_overview_snap: MatchDesignLabel;
                matches_overview_snap: MatchDesignLabel;
                calender_overview_snap: MatchDesignLabel;
                detail: {
                    date: string;
                    status: string;
                    cancel: string;
                    delete: string;
                    duration: string;
                    location: string;
                    end_date: string;
                    end_match: string;
                    start_date: string;
                    edit_match: string;
                    record_match: string;
                    cancellation_reason: string;
                    match_participant_snap: MatchDesignLabel;
                    table: {
                        headers: {
                            participant: string;
                        };
                    };
                };
                table: {
                    headers: {
                        date: string;
                        match: string;
                        status: string;
                        location: string;
                        participants: string;
                    };
                    empty_label_parts: {
                        or: string;
                        have_no: string;
                        add_new: string;
                        update_stage: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                };
                dialog: {
                    cancel: {
                        desc: string;
                        title: string;
                        reason: string;
                        cancel_successful: string;
                    };
                    delete: {
                        desc: string;
                        title: string;
                        delete: string;
                        successfully_deleted: string;
                        alternative: {
                            label: string;
                            location: string;
                        };
                        how_it_affects: {
                            label: string;
                            location: string;
                        };
                    };
                    end_match: {
                        desc: string;
                        title: string;
                        successfully_ended: string;
                    };
                    add_update_match: {
                        date: string;
                        match: string;
                        add_new: string;
                        location: string;
                        duration: string;
                        end_date: string;
                        start_date: string;
                        create_new: string;
                        match_value: string;
                        update_match: string;
                        delete_match: string;
                        create_match: string;
                        create_successful: string;
                        update_successful: string;
                        update_this_match: string;
                        create_in_bracket: string;
                        bracket_affection: string;
                        bracket_update_affection: string;
                        search_and_select_participant: string;
                    };
                    record_match_results: {
                        desc: string;
                        title: string;
                        losers: string;
                        drawers: string;
                        winners: string;
                        update_result: string;
                        upload_proofs: string;
                        accepted_proofs: string;
                        submitted_results: string;
                        actual_result_scoring: string;
                        submitted_results_and_proofs: string;
                        match_result_updated_successfully: string;
                    };
                };
            };
            finance: {
                export: string;
                view_all: string;
                currency: string;
                all_trans: string;
                date_range: string;
                recent_trans: string;
                source: FormControls;
                earning_chart: string;
                scroll_for_more: string;
                allocate_capital: string;
                click_to_withdraw: string;
                projected_returns: string;
                view_detailed_anals: string;
                configure_host_earnings: string;
                source_labels: NoseurObject<string>;
                table: {
                    headers: {
                        date: string;
                        source: string;
                        amount: string;
                        description: string;
                        platform_charge: string;
                    };
                    empty_label_parts: {
                        no_trans: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                };
                dialog: {
                    unknown_error: string;
                    allocate_capital: {
                        desc: string;
                        title: string;
                        amount: string;
                        amount2: string;
                        allocate: string;
                        currency: string;
                        charge_fee: string;
                        total_to_pay: string;
                        select_currency: string;
                        all_to_know: {
                            label: string;
                            location: string;
                        };
                    };
                    allocation_status: {
                        failed_desc: string;
                        success_desc: string;
                        failed_title: string;
                        success_title: string;
                    };
                    transfer_to_manager: {
                        desc: string;
                        title: string;
                        transfer: string;
                        tour_name: string;
                        amount_max: string;
                        enter_tour_name: string;
                        transfer_successful: string;
                        change_host_earners: string;
                        who_received_how_much: string;
                    };
                };
            };
            analytics: {
                new: string;
                total: string;
                ended: string;
                export: string;
                trophy: string;
                matches: string;
                removed: string;
                ongoing: string;
                view_all: string;
                upcoming: string;
                withdrawn: string;
                prize_pool: string;
                date_range: string;
                ads_earning: string;
                unique_parts: string;
                recent_trans: string;
                participants: string;
                trophy_given: string;
                matches_anals: string;
                total_earning: string;
                parts_removed: string;
                earning_anals: string;
                matches_compl: string;
                earned_frm_ads: string;
                physical_prize: string;
                merch_earnings: string;
                matches_played: string;
                view_all_parts: string;
                no_parts_found: string;
                monetary_prize: string;
                ongoing_matches: string;
                parts_withdrawn: string;
                tournament_anal: string;
                parts_registered: string;
                prize_pool_anals: string;
                upcoming_matches: string;
                recent_prize_pool: string;
                participants_anals: string;
                view_all_prize_pool: string;
                view_all_match_fixs: string;
                recent_participants: string;
                top_earning_managers: string;
                earned_frm_tour_regs: string;
                registration_earning: string;
                earned_within_x_days: string;
                physical_prize_given: string;
                recent_match_fixtures: string;
                earned_frm_merch_sales: string;
                prize_pool_distributed: string;
                participants_registered: string;
                monetary_pool_distributed: string;
                total_priz_pool_distributed: string;

            };
            result_prize_distro: {
                export: string;
                trophies: string;
                activities: string;
                top_winner: string;
                add_new_prize: string;
                announce_result: string;
                historic_prize_pool: string;
                query_inputs: FormControls[];
                distribute_prize_pool: string;
                view_historical_prize_pool: string;
                prize_pool_media_snap: MatchDesignLabel;
                top_winners_media_snap: MatchDesignLabel;
                trophies_prize_media_snap: MatchDesignLabel;
                historic_prizes_media_snap: MatchDesignLabel;
                details: {
                    winner: string;
                    delete: string;
                    edit_prize: string;
                    how_to_claim: string;
                    monetary_prizes: string;
                    physical_prizes: string;
                    rightful_winner: string;
                    projected_winner: string;
                    validate_physical_prize: string;
                    manually_selected_winner: string;
                    manually_override_winner: string;
                    type_labels: NoseurObject<string>;
                    remove_manually_override_winner: string;
                    prize_winner_media_snap: MatchDesignLabel;
                    trophy_prize_media_snap: MatchDesignLabel;
                    top_winner_prize_media_snap: MatchDesignLabel;
                    prize_selected_winner_media_snap: MatchDesignLabel;
                    prize_rightful_winner_media_snap: MatchDesignLabel;
                    top_winner_projected_winner_media_snap: MatchDesignLabel;
                    trophy_prize_projected_winner_media_snap: MatchDesignLabel;
                };
                table: {
                    headers: {
                        type: string;
                        name: string;
                        winner: string;
                        monetary_prize: string;
                        presentment_date: string;
                    };
                    empty_label_parts: {
                        have_no: string;
                        add_new: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                };
                dialog: {
                    announce_result: {
                        desc: string;
                        title: string;
                        trophies: string;
                        announce: string;
                        view_leaderboard: string;
                        tournament_winners: string;
                        announcement_successful: string;
                        what_happens: {
                            label: string;
                            location: string;
                        };
                    };
                    distribute: {
                        desc: string;
                        title: string;
                        distribute: string;
                        distribution_successful: string;
                        authorizing: {
                            label: string;
                            location: string;
                        };
                    };
                    delete: {
                        desc: string;
                        title: string;
                        delete: string;
                        top_winner: string;
                        see_how: {
                            label: string;
                            location: string;
                        };
                    };
                    manually_override_winner: {
                        desc: string;
                        title: string;
                        change: string;
                        new_winner: string;
                        to_confirm: string;
                        top_winner: string;
                        successfully_attached_winner: string;
                        see_how: {
                            label: string;
                            location: string;
                        };
                        alternative: {
                            label: string;
                            location: string;
                        };
                    };
                    remove_manual_winner: {
                        desc: string;
                        title: string;
                        proceed: string;
                        top_winner: string;
                        successfully_removed_attached_winner: string;
                    };
                    validate_claim_physical_prize: {
                        desc: string;
                        title: string;
                        validate: string;
                        claim_code: string;
                        successfully_claimed: string;
                        claiming: {
                            label: string;
                            location: string;
                        };
                    };
                };
            };
            scoring: {
                add_custom_score: string;
                query_inputs: FormControls[];
                edit_score_and_tie_breaker: string;
                score_leader_snap: MatchDesignLabel;
                all_scoring_overview_snap: MatchDesignLabel;
                table: {
                    headers: {
                        name: string;
                        type: string;
                        leader: string;
                    };
                };
                detail: {
                    delete: string;
                    individual: string;
                    participant: string;
                    edit_scoring: string;
                    custom_score: string;
                    record_result: string;
                    tournament_score: string;
                    score_overview_snap: MatchDesignLabel;
                    score_type_label: NoseurObject<string>;
                    score_leaderboard_snap: MatchDesignLabel;
                    participant_place_snap: MatchDesignLabel;
                    table: {
                        headers: {
                            place: string;
                            old_place: string;
                            new_place: string;
                            participant: string;
                        };
                    };
                };
                dialog: {
                    maximize_leaderboard: {
                        desc: string;
                        title: string;
                    };
                    create_update: {
                        name: string;
                        create: string;
                        update: string;
                        applies_to: string;
                        create_desc: string;
                        update_desc: string;
                        attach_trophy: string;
                        create_custom_score: string;
                        update_custom_score: string;
                        choose_scoring_system: string;
                        show_on_tour_public_pages: string;
                        applies_to_options: {
                            value: string;
                            label: string;
                        }[];
                        what_is: {
                            label: string;
                            location: string;
                        };
                    };
                    delete: {
                        desc: string;
                        title: string;
                        delete: string;
                        enter_name: string;
                        successfully_deleted: string;
                        to_confirm_enter_name: string;
                        see_how: {
                            label: string;
                            location: string;
                        };
                    };
                    manually_record_score: {
                        desc: string;
                        title: string;
                        record_result: string;
                        table: {
                            headers: {
                                participant: string;
                            };
                        };
                    };
                };
            };
            notifications: {
                show_all_edition_notifications: string;
            };
            settings: {
                general: {
                    title: string;
                    social_networks: {
                        title: string;
                        update_success: string;
                        enter_tour_usernames: string;
                    };
                    banner_medias: {
                        title: string;
                        banner_1: string;
                        banner_2: string;
                        banner_3: string;
                        make_primary: string;
                        update_success: string;
                    };
                    website_pages: {
                        home: string;
                        title: string;
                        label: string;
                        merch: string;
                        about: string;
                        events: string;
                        gallery: string;
                        visible: string;
                        schedule: string;
                        trophies: string;
                        home_page: string;
                        merch_page: string;
                        about_page: string;
                        events_page: string;
                        leaderboard: string;
                        gallery_page: string;
                        participants: string;
                        schedule_page: string;
                        trophies_page: string;
                        leaderboard_page: string;
                        participants_page: string;
                        update_successful: string;
                    };
                    mobile_app_feature: {
                        merch: string;
                        about: string;
                        title: string;
                        label: string;
                        search: string;
                        events: string;
                        enabled: string;
                        settings: string;
                        schedule: string;
                        messages: string;
                        earnings: string;
                        trophies: string;
                        dashboard: string;
                        leaderboard: string;
                        participants: string;
                        notifications: string;
                        update_successful: string;
                    };
                    tour_settings: {
                        desc: string;
                        name: string;
                        title: string;
                        public_id: string;
                        op_country: string;
                        close_tour: string;
                        description: string;
                        update_success: string;
                        select_ed_logo: string;
                        reactivate_tour: string;
                        dialog: {
                            confirm_tour: {
                                tour_name: string;
                                to_continue: string;
                                name_does_not_match: string;
                            };
                            close: {
                                desc: string;
                                title: string;
                                success: string;
                                what_happens: {
                                    article_link: string;
                                    article_label: string;
                                };
                            };
                            reactivate: {
                                desc: string;
                                title: string;
                                success: string;
                            };
                        };
                    };
                };
                home: {
                    title: string;
                    success: string;
                    enable_ad: string;
                    show_sponsor: string;
                    show_top_review: string;
                    show_top_gallery: string;
                    show_twitter_feed: string;
                    show_twitch_stream: string;
                    show_instagram_feed: string;
                    upload_sponsor_logos: string;
                    show_top_participants: string;
                    show_price_pool_overview: string;
                    dialog: {
                        small_preview: string;
                        large_preview: string;
                        medium_preview: string;
                        select_another: string;
                        no_media_selected: string;
                        upload_home_sponsor_logo: string;
                    };
                };
                events: {
                    title: string;
                };
                merch: {
                    title: string;
                    update_successful: string;
                };
                participants: {
                    title: string;
                    success: string;
                    enable_ad: string;
                    show_current: string;
                    show_previous: string;
                };
                schedule: {
                    title: string;
                    success: string;
                    enable_ad: string;
                    show_past_match: string;
                    show_upcoming_ongoing_match: string;
                };
                gallery: {
                    title: string;
                    update_successful: string;
                };
                leaderboard: {
                    title: string;
                    enable_ad: string;
                    show_form: string;
                    show_wld_records: string;
                    update_successful: string;
                    show_all_custom_scores: string;
                };
                trophies: {
                    title: string;
                    enable_ad: string;
                    update_successful: string;
                    enable_cross_filter: string;
                    show_monetary_prize: string;
                    show_physical_prize: string;
                };
                about: {
                    title: string;
                    visibilities: {
                        title: string;
                        enable_ad: string;
                        show_faqs: string;
                        show_rules: string;
                        show_details: string;
                        show_reviews: string;
                        show_sponsors: string;
                        update_successful: string;
                    };
                    rules: {
                        title: string;
                        enter_rules: string;
                        update_successful: string;
                    };
                    details: {
                        title: string;
                        tour_details: string;
                        update_successful: string;
                    };
                    faqs: {
                        title: string;
                        answer: string;
                        add_faq: string;
                        question: string;
                        enable_ad: string;
                        update_successful: string;
                        empty_label_parts: {
                            create: string;
                            an_faq: string;
                            no_faq_found: string;
                        };
                        sorts: {
                            label: string;
                            value: string;
                        }[];
                        dialog: {
                            add_update: {
                                add: string;
                                update: string;
                                delete: string;
                                answer: string;
                                add_faq: string;
                                question: string;
                                update_faq: string;
                                add_successful: string;
                                update_successful: string;
                            };
                            delete: {
                                desc: string;
                                title: string;
                                successful: string;
                            };
                        };
                    };
                };
                website: {
                    title: string;
                    design_update_successful: string;
                };
                mobile_app: {
                    title: string;
                };
            };
        };
        analytics: {
            title: string;
            programs: string;
            host_anals: string;
            tournaments: string;
            total_earnings: string;
            tournament_hosted: string;
            top_earning_managers: string;
            earned_within_x_days: string;
            tournaments_by_earnings: string;
            tournaments_by_participant: string;
            top_earning: {
                program: string;
                location: string;
                recurring: string;
                unique_participants: string;
                empty_label_parts: {
                    create: string;
                    a_tour_to_be: string;
                    no_tours_found: string;
                };
            };
            earnings: {
                you: string;
                owner: string;
                transactions: string;
                ads_earnings: string;
                total_earnings: string;
                merch_earnings: string;
                earned_from_merchs: string;
                tournament_earnings: string;
                earned_from_rivtn_ads: string;
                earned_from_tours_regs: string;
            };
            transactions: {
                view_all_trans: string;
                last_10_earnings: string;
                table: {
                    headers: {
                        date: string;
                        amount: string;
                        source: string;
                        recipient: string;
                    };
                };
                empty_label_parts: {
                    create: string;
                    a_tours: string;
                    create_merch: string;
                    to_gen_income: string;
                    no_earns_found: string;
                };
                sorts: {
                    label: string;
                    value: string;
                }[];
            };
            chart_labels: {
                programs: string;
                earnings: string;
                tournaments: string;
            };
        };
        settings: {
            host: string;
            general: {
                title: string;
                basic: {
                    name: string;
                    title: string;
                    host_id: string;
                    homepage: string;
                    id_number: string;
                    issue_date: string;
                    short_info: string;
                    visibility: string;
                    email_addr: string;
                    expiry_date: string;
                    means_of_id: string;
                    base_country: string;
                    upload_proof: string;
                    update_success: string;
                    short_info_desc: string;
                    means_of_id_ctrl: FormControls;
                };
                managers: {
                    owner: string;
                    title: string;
                    send_invite: string;
                    enter_user_id: string;
                    manager_since: string;
                    transfer_host_ownership: string;
                    dialog: {
                        transfer: {
                            desc: string;
                            title: string;
                            host_name: string;
                            search_select_man: string;
                            ownership_transfer_successful: string;
                        };
                        invite: {
                            desc: string;
                            title: string;
                            host_name: string;
                            send_invite: string;
                        };
                        remove: {
                            desc: string;
                            title: string;
                            remove: string;
                            man_name: string;
                            remove_successful: string;
                        };
                    };
                };
                host_earnings: {
                    title: string;
                    who_receives: string;
                    update_success: string;
                    info: {
                        part_1: string;
                        part_2: string;
                        part_3: string;
                        part_4: string;
                        part_5: string;
                    };
                    receivers: {
                        owner_you: string;
                        selected_managers: string;
                        split_equally_bet_mans: string;
                    };
                    accept_policy: {
                        accept: string;
                        omne_policy: {
                            label: string;
                            location: string;
                        };
                    };
                };
                social_networks: {
                    title: string;
                    update_success: string;
                    enter_host_usernames: string;
                };
                banner_medias: {
                    title: string;
                };
                website_pages: {
                    home: string;
                    blog: string;
                    title: string;
                    label: string;
                    tours: string;
                    merch: string;
                    about: string;
                    events: string;
                    visible: string;
                    home_page: string;
                    blog_page: string;
                    tours_page: string;
                    merch_page: string;
                    about_page: string;
                    events_page: string;
                    update_successful: string;
                };
                mobile_app_feature: {
                    blog: string;
                    merch: string;
                    about: string;
                    title: string;
                    label: string;
                    search: string;
                    events: string;
                    enabled: string;
                    settings: string;
                    messages: string;
                    earnings: string;
                    dashboard: string;
                    tournaments: string;
                    notifications: string;
                    update_successful: string;
                };
            };
            manage_tournaments: {
                name: string;
                title: string;
                status: string;
                currency: string;
                active_date: string;
                participants: string;
                create_tournament: string;
                table: {
                    headers: {
                        name: string;
                        status: string;
                        prev_earns: string;
                        unique_partis: string;
                        forecast_earns: string;
                        last_date_updated: string;
                    };
                };
                empty_label_parts: {
                    create: string;
                    a_tour: string;
                    no_tours_found: string;
                };
                statuses_labels: {
                    draft: string;
                    ended: string;
                    closed: string;
                    pending: string;
                    ongoing: string;
                    upcoming: string;
                    cancelled: string;
                    forfeited: string;
                    in_registration: string;
                };
                sorts: {
                    label: string;
                    value: string;
                }[];
                statuses?: {
                    label: string;
                    value: string;
                }[];
            };
            home: {
                title: string;
                enable_ads: string;
                show_sponsor: string;
                show_twitter_x: string;
                show_top_tours: string;
                show_insta_feed: string;
                update_successful: string;
                show_twitch_stream: string;
                upload_host_sponsors: string;
                upload_host_sponsor_logo: string;
                show_top_best_tours_reviews: string;
            };
            hero: {
                title: string;
                enable_ad_label: string;
                update_successful: string;
            };
            merch: {
                title: string;
                update_successful: string;
            };
            tournaments: {
                title: string;
                enable_ads: string;
                show_ended_tours: string;
                show_cancel_tours: string;
                show_future_tours: string;
                show_closed_tours: string;
                update_successful: string;
                show_ongoing_tours: string;
            };
            events: {
                title: string;
                status: string;
                details: string;
                title_l: string;
                add_event: string;
                enable_ads: string;
                update_successful: string;
                empty_label_parts: {
                    create: string;
                    an_event: string;
                    no_event_found: string;
                };
                statuses_labels: {
                    ended: string;
                    ongoing: string;
                    unknown: string;
                    upcoming: string;
                };
                sorts: {
                    label: string;
                    value: string;
                }[];
                statuses?: {
                    label: string;
                    value: string;
                }[];
                dialog: {
                    add_update: {
                        add: string;
                        title: string;
                        update: string;
                        delete: string;
                        details: string;
                        location: string;
                        end_date: string;
                        add_event: string;
                        start_date: string;
                        action_loc: string;
                        action_label: string;
                        update_event: string;
                        add_successful: string;
                        update_successful: string;
                        choose_feature_img: string;
                    };
                    delete: {
                        desc: string;
                        title: string;
                        successful: string;
                    };
                };
            };
            blog: {
                title: string;
                categories: {
                    name: string;
                    title: string;
                    enable_ads: string;
                    description: string;
                    create_category: string;
                    update_successful: string;
                    empty_label_parts: {
                        create: string;
                        no_cat: string;
                        a_new_cat: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                    dialog: {
                        create_update: {
                            name: string;
                            create: string;
                            update: string;
                            delete: string;
                            update_cat: string;
                            choose_logo: string;
                            description: string;
                            create_new_cat: string;
                            add_successful: string;
                            update_successful: string;
                        };
                        delete: {
                            desc: string;
                            title: string;
                            successful: string;
                        };
                    };
                };
                articles: {
                    title: string;
                    title_v: string;
                    content: string;
                    category: string;
                    enable_ads: string;
                    create_article: string;
                    update_successful: string;
                    empty_label_parts: {
                        create: string;
                        no_art: string;
                        a_new_art: string;
                    };
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                    dialog: {
                        create_edit: {
                            title: string;
                            update: string;
                            delete: string;
                            publish: string;
                            view_comments: string;
                            update_or_delete: string;
                            create_team_news: string;
                            update_successful: string;
                            delete_successful: string;
                            publish_successful: string;
                            content_in_markdown: string;
                            search_and_select_a_cat: string;
                        };
                        delete: {
                            desc: string;
                            title: string;
                        };
                    };
                };
                comment: {
                    reply: string;
                    title: string;
                    delete: string;
                    author: string;
                    article: string;
                    content: string;
                    enable_ads: string;
                    no_comment: string;
                    sorts: {
                        label: string;
                        value: string;
                    }[];
                };
            };
            about: {
                title: string;
                visibilities: {
                    title: string;
                    enable_ad: string;
                    show_addr: string;
                    show_people: string;
                    show_sponsors: string;
                    update_successful: string;
                    show_contact_phone_emails: string;
                };
                phy_address: {
                    city: string;
                    title: string;
                    state: string;
                    country: string;
                    address: string;
                    address_1: string;
                    address_2: string;
                    update_successful: string;
                };
                email_phone: {
                    title: string;
                    email: string;
                    phone: string;
                    email_phone_1: string;
                    email_phone_2: string;
                    update_successful: string;
                };
                people: {
                    you: string;
                    role: string;
                    title: string;
                    joined: string;
                    update_success: string;
                    manager: {
                        twitch: string;
                        discord: string;
                        country: string;
                        youtube: string;
                        facebook: string;
                        instagram: string;
                        twitter_x: string;
                        state_region: string;
                        display_name: string;
                        show_profile_link: string;
                        profile_description: string;
                        hide_on_manager_page: string;
                    };
                };
                company_info: {
                    title: string;
                    update_success: string;
                };
            };
            website: {
                title: string;
                design_update_successful: string;
            };
            mobile_app: {
                title: string;
            };
        };
    };
    earnings: {
        earnings: string;
        view_all: string;
        earning_chart: string;
        scroll_for_more: string;
        recent_transactions: string;
        click_card_for_more: string;
        date_range_input: FormControls;
        transactions: {
            title: string;
            export: string;
            no_transaction: string;
            type_input: FormControls;
            currency_input: FormControls;
            table: {
                headers: {
                    tax: string;
                    date: string;
                    source: string;
                    amount: string;
                    description: string;
                    platform_charge: string;
                    amount_received: string;
                };
            };
            sorts: {
                label: string;
                value: string;
            }[];
        };
        currency_earnings: {
            pending: string;
            withdraw: string;
            available: string;
            in_progress: string;
            last_payment: string;
            latest_earning: string;
            recent_earnings: string;
            expected_earning: string;
            withdraw_balance: string;
            recent_withdrawal: string;
            withdrawal_settings: string;
            recent_earnings_note: string;
            available_after_x_hours: string;
            change_withdrawal_settings: string;
            pending_balance_and_adjustment: string;
            pending_balance_and_adjustment_note: string;
            in_progress_table: {
                empty_label_parts: {
                    to_have: string;
                    not_expecting: string;
                    host_tournament: string;
                };
                headers: {
                    date: string;
                    source: string;
                    amount: string;
                    tax_charge: string;
                    platform_charge: string;
                    expected_earning: string;
                };
            };
            pending_table: {
                empty_label_parts: {
                    to_have: string;
                    no_pending: string;
                    setup_merch: string;
                    host_tournament: string;
                    update_ad_settings: string;
                };
                headers: {
                    tax: string;
                    date: string;
                    source: string;
                    amount: string;
                    description: string;
                    platform_charge: string;
                    expected_amount: string;
                };
            };
            available_table: {
                no_withdrawal: string;
                auto_send_info: string;
                no_auto_withdrawal_parts: {
                    no_auto: string;
                    to_enable: string;
                    update_settings: string;
                };
                headers: {
                    tax: string;
                    date: string;
                    amount: string;
                    description: string;
                    payout_method: string;
                    platform_charge: string;
                    amount_received: string;
                };
            };
        };
        withdrawal_settings: {
            to: string;
            title: string;
            next_on: string;
            withdraw: string;
            schedule: string;
            last_payment: string;
            edit_schedule: string;
            available_balance: string;
            payout: {
                preferred: string;
                payout_method: string;
                add_new_method: string;
                no_payout_parts: {
                    to_be: string;
                    no_payout: string;
                    add_payout_method: string;
                };
                popover: {
                    remove: string;
                    set_as_preferred: string;
                };
            };
            dialog: {
                remove_payout_method: {
                    desc: string;
                    title: string;
                    remove_successful: string;
                };
                remove_withdrawal_settings: {
                    desc: string;
                    title: string;
                    remove_successful: string;
                };
                withdraw: {
                    max: string;
                    title: string;
                    change: string;
                    amount: string;
                    token_sent: string;
                    token_error: string;
                    enter_token: string;
                    resend_token: string;
                    amount_lower: string;
                    payout_method: string;
                    invalid_amount: string;
                    withdrawal_fee: string;
                    amount_greater: string;
                    you_will_receive: string;
                    confirmation_token: string;
                    token_not_received: string;
                    amount_greater_available: string;
                };
                select_payout_method: {
                    title: string;
                    use_selected: string;
                };
                edit_schedule: {
                    title: string;
                    min_of: string;
                    remove: string;
                    or_more: string;
                    schedule: string;
                    add_successful: string;
                    withdrawal_fee: string;
                    when_balance_is: string;
                    leave_reserve_of: string;
                    select_all_fields: string;
                    you_can_withdraw_any_time: string;
                    preferred_payment_schedule: string;
                    next_payment_based_on_schedule: string;
                    learn_more: {
                        label: string;
                        location: string;
                    };
                };
                add_payout: {
                    add: string;
                    title: string;
                    currency: string;
                    other_methods: string;
                    add_successful: string;
                    withdrawal_fee: string;
                    per_withdrawal: string;
                    recommended_for: string;
                    default_successful: string;
                    enter_payout_details: string;
                    current_exchange_rate: string;
                    missing_a_payout_field: string;
                    details_translation: NoseurObject<string>;
                };
                withdrawal_successful: {
                    desc: string;
                    title: string;
                };
            };
        };
    };
};

export type LanguageMap = { [key: string]: Labels };

const languages: LanguageMap = {
    en_UK,
};

let i18nData: Labels;
const urlSearchParams = new URLSearchParams(window.location.search);
let defaultLanguage: string = (urlSearchParams.get("lang") ?? CacheManager.get("lang") ?? "en_UK");

export const i18nManager = {

    Labels: i18nData!,
    LabelsLoaded: false,
    ActiveLanguage: defaultLanguage,
    Languages: [
        {
            value: "en_US",
            short_name: "EN US",
            label: "English US",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e2/Flag_of_the_United_States_%28Pantone%29.svg/510px-Flag_of_the_United_States_%28Pantone%29.svg.png",
        },
        {
            value: "en_UK",
            short_name: "EN",
            label: "English UK",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Flag_of_the_United_Kingdom_%283-5%29.svg/350px-Flag_of_the_United_Kingdom_%283-5%29.svg.png",
        },
        {
            value: "es",
            short_name: "ES",
            label: "El Español",
            icon: "https://upload.wikimedia.org/wikipedia/en/thumb/9/9a/Flag_of_Spain.svg/400px-Flag_of_Spain.svg.png",
        },
        {
            value: "fr",
            short_name: "FR",
            label: "Française",
            icon: "https://upload.wikimedia.org/wikipedia/en/thumb/c/c3/Flag_of_France.svg/510px-Flag_of_France.svg.png",
        },
        {
            value: "ar",
            label: "Arabic",
            short_name: "AR",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/Flag_of_Saudi_Arabia.svg/510px-Flag_of_Saudi_Arabia.svg.png",
        },
    ],
    CountriesAndCurrencies: [
        {
            value: "NGA",
            label: "Nigeria",
            currency_sign: "₦",
            currency_code: "NGN",
            currency_name: "Naira",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/79/Flag_of_Nigeria.svg/260px-Flag_of_Nigeria.svg.png",
        },
        {
            value: "GHA",
            label: "Ghana",
            currency_sign: "₵",
            currency_code: "GHS",
            currency_name: "Cedi",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Flag_of_Ghana.svg/250px-Flag_of_Ghana.svg.png",
        },
        {
            value: "KEN",
            label: "Kenya",
            currency_sign: "KES",
            currency_code: "KES",
            currency_name: "Kenya Shillings",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/49/Flag_of_Kenya.svg/800px-Flag_of_Kenya.svg.png",
        },
        {
            value: "USA",
            label: "USA",
            currency_sign: "$",
            currency_code: "USD",
            currency_name: "US Dollars",
            icon: "https://st2.depositphotos.com/1000487/10460/v/950/depositphotos_104607398-stock-illustration-united-states-flag.jpg",
        },
        {
            value: "IND",
            label: "India",
            currency_sign: "₹",
            currency_code: "INR",
            currency_name: "Rupees",
            icon: "https://upload.wikimedia.org/wikipedia/en/thumb/4/41/Flag_of_India.svg/1200px-Flag_of_India.svg.png",
        },
        {
            value: "GBR",
            label: "United Kingdom",
            currency_sign: "£",
            currency_code: "GBP",
            currency_name: "Pound sterling",
            icon: "https://upload.wikimedia.org/wikipedia/en/a/ae/Flag_of_the_United_Kingdom.svg",
        },
        {
            value: "ZAF",
            label: "South Africa",
            currency_sign: "Z",
            currency_code: "ZAR",
            currency_name: "South African Rand",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/af/Flag_of_South_Africa.svg/640px-Flag_of_South_Africa.svg.png",
        },
    ],

    switchLanguage(lang: string) {
        urlSearchParams.set("lang", lang);
        CacheManager.insecurePut("lang", lang);
        window.location.search = urlSearchParams.toString();
    },

    getCountryCurrency(countryCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.value === countryCode) return entry.currency_code;
        }
        return "NGN";
    },

    getCountryName(countryCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.value === countryCode) return entry.label;
        }
        return "Nigeria";
    },

    getCountryNameByCurrencyCode(currencyCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.currency_code === currencyCode) return entry.label;
        }
        return "Nigeria";
    },

    getCurrencyName(currencyCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.currency_code === currencyCode) return entry.currency_name;
        }
        return "Naira";
    },

    getCountryCurrencyName(countryCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.value === countryCode) return entry.currency_name;
        }
        return "Naira";
    },

    getCurrencySign(currencyCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.currency_code === currencyCode) return entry.currency_sign;
        }
        return "₦";
    },

    getCurrencyCountryName(currencyCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.currency_code === currencyCode) return entry.label;
        }
        return "Nigeria";
    },

    getCurrencyCountryFlag(currencyCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.currency_code === currencyCode) return entry.icon;
        }
        return "https://upload.wikimedia.org/wikipedia/commons/thumb/7/79/Flag_of_Nigeria.svg/260px-Flag_of_Nigeria.svg.png";
    },

    getCountryCurrencySign(countryCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.value === countryCode) return entry.currency_sign;
        }
        return "₦";
    },

    getCurrencyCountryCode(currencyCode: string) {
        for (const entry of i18nManager.CountriesAndCurrencies) {
            if (entry.currency_code === currencyCode) return entry.value;
        }
        return "NGA";
    },

};

if (!i18nManager.LabelsLoaded) {
    i18nData = languages[defaultLanguage.replace('-', '_')] || languages["en_UK"];
    i18nData.timezone = defaultLanguage;
    i18nManager.Labels = i18nData;
    i18nManager.LabelsLoaded = true;
}

i18nManager.Labels.tournaments.filter.form.find((e) => e.name === "participant_type")!.options = i18nManager.Labels.tournaments.team_types;
